import { GrafferPermissions, Roles, PermissionMap } from "../rbac/rbac";
import { GroupPermissionAttached } from "../group/grp_resource";
import { OrgUsingFeatureType } from "../organization/org-with-using-features";
import { PaymentMethod } from "@graffer-inc/cloud-gov-core";
import {
  OrgProductRawType,
  OrgProductType,
  PlanTypeLiterals,
  ProductCodeTypeLiterals,
} from "../organization/org-product";
import { UserAuthMethod } from "../user/user";
import { PermissionSet } from "../rbac/permission_set";

type AccountType = "OrgAccount" | "GrafferStaffAccount";

export type AuthType = {
  error?: Error;
  status?: number;
  isAuthed: boolean;
  data?: {
    id: string;
    name: string;
    accountType: AccountType;
    roles: Array<Roles>;
    orgId: string;
    orgSlug: string;
    orgName: string;
    permissions?: GrafferPermissions[];
    groupIds: Array<string>;
    requirePasswordChange: boolean;
    orgUsingFeature: OrgUsingFeatureType;
    orgProducts: OrgProductType[];
    groupDelegatedPermissions: { [key: string]: GrafferPermissions[] };
    authMethod: UserAuthMethod;
  };
  lastChecked?: Date;
};

export type CheckAuthDashboardUserType = {
  id: string;
  accountType: AccountType;
  name: string;
  roles: Array<Roles>;
  orgId: string;
  groupIds: Array<string>;
  permissions: GrafferPermissions[];
  requirePasswordChange: boolean;
  groupDelegatedPermissions: { [key: string]: GrafferPermissions[] };
  orgUsingFeature: OrgUsingFeatureType;
  orgProducts: OrgProductRawType[] | undefined;
  authMethod: UserAuthMethod;
};

export type CheckAuthDashboardType = {
  isAuthed: boolean;
  user: CheckAuthDashboardUserType;
};

export class Auth {
  public readonly error?: Error;
  public readonly status?: number;
  public readonly isAuthed: boolean;
  public readonly data?: {
    readonly id: string;
    readonly name: string;
    readonly accountType: AccountType;
    readonly roles: Array<Roles>;
    readonly orgId: string;
    readonly orgSlug: string;
    readonly orgName: string;
    readonly permissions?: GrafferPermissions[];
    readonly groupIds: Array<string>;
    readonly requirePasswordChange: boolean;
    readonly orgUsingFeature: OrgUsingFeatureType;
    readonly orgProducts: OrgProductType[];
    readonly groupDelegatedPermissions: { [key: string]: GrafferPermissions[] };
    readonly authMethod: UserAuthMethod;
  };
  public readonly lastChecked?: Date;

  constructor(values: AuthType) {
    this.error = values.error;
    this.status = values.status;
    this.isAuthed = values.isAuthed;
    this.data = values.data;
    this.lastChecked = values.lastChecked;
  }

  static noAuth(): Auth {
    return new Auth({
      isAuthed: false,
    });
  }

  static serviceProviderAuth(
    status: number,
    isAuthed: boolean,
    user: CheckAuthDashboardUserType,
  ): Auth {
    return new Auth({
      status,
      isAuthed,
      data: {
        ...user,
        roles: ["GrafferStaff"],
        orgId: "",
        orgSlug: "",
        orgName: "",
        groupIds: [],
        permissions: PermissionMap["GrafferStaff"],
        groupDelegatedPermissions: {},
        orgProducts: [],
      },
      lastChecked: new Date(),
    });
  }

  hasRole(role: Roles): boolean {
    return !!this.data?.roles && this.data.roles.includes(role);
  }

  hasPermissions(requirePermissions: GrafferPermissions) {
    if (requirePermissions.startsWith("proc_management")) {
      // TODO: Fix Me!
      return this.hasProcManagementRelatedPermission(requirePermissions);
    }

    return (
      this.data?.permissions &&
      this.data.permissions.indexOf(requirePermissions) !== -1
    );
  }

  // グループに属する特定のリソース(target)に対して、特定の権限(requirePerms)を持っているかを判定する
  checkGroupPermission(
    target: GroupPermissionAttached,
    requirePerms: GrafferPermissions[],
  ) {
    if (requirePerms.find((p) => !this.hasPermissions(p))) {
      return false;
    }
    if (this.hasRole("Admin")) {
      // Adminはグループに関わらず、どのグループも操作可能
      return true;
    }
    for (const groupId of this.groupIDs) {
      const attached = target.resolvePermission(groupId);
      const intersection = attached.intersection(
        this.data?.groupDelegatedPermissions[groupId] || [],
      );
      if (intersection.containsAll(requirePerms)) {
        return true;
      }
    }
    return false;
  }

  // リソースを指定せず、ユーザーのグループに対しての権限(グループ管理者/グループスタッフ)を確認する
  checkGroupPermissionWithoutTarget(requirePerms: GrafferPermissions[]) {
    if (requirePerms.find((p) => !this.hasPermissions(p))) {
      return false;
    }
    if (this.hasRole("Admin")) {
      // Adminはグループに関わらず、どのグループも操作可能
      return true;
    }
    for (const groupId of this.groupIDs) {
      const groupPermission = new PermissionSet(
        this.data?.groupDelegatedPermissions[groupId] || [],
      );
      if (groupPermission.containsAll(requirePerms)) {
        return true;
      }
    }
    return false;
  }

  get usePayment() {
    return this.data?.orgUsingFeature.usePayment;
  }

  get usePaymentPayeasy() {
    return this.data?.orgUsingFeature.usePaymentPayeasy;
  }

  get usePaymentPayPay() {
    return this.data?.orgUsingFeature.usePaymentPayPay;
  }

  get acceptedPaymentMethods(): PaymentMethod[] {
    if (!this.usePayment) {
      return [];
    }

    const methods: PaymentMethod[] = ["credit_card"];
    if (this.usePaymentPayPay) {
      methods.push("paypay");
    }
    if (this.usePaymentPayeasy) {
      methods.push("payeasy");
    }

    return methods;
  }

  // NOTE(adshidtadka): GrafferStaffという名前は非推奨になりServiceProviderAccountという名前に統合された(https://graffer.esa.io/posts/1445)
  // それに伴ってメソッド名のみ先んじてisServiceProviderAccountという名前になっている
  isServiceProviderAccount() {
    return this.data?.accountType === "GrafferStaffAccount";
  }

  isPortalAvailable() {
    return this.isProductAvailable("Portal");
  }

  isProcedureMasterAvailable() {
    return this.isPortalStandardPlan() || this.isPortalLightPlan();
  }

  isPortalLightPlan() {
    return (
      this.isProductAvailable("Portal") &&
      !!this.data &&
      this.data.orgProducts.some((v) => ["Light"].includes(v.plan))
    );
  }

  isPortalStandardPlan() {
    return (
      this.isProductAvailable("Portal") &&
      !!this.data &&
      this.data.orgProducts.some((v) => ["Standard"].includes(v.plan))
    );
  }

  // ProductCode と Plan を受け取り、当該テナントユーザーが
  // 製品を利用可能かどうか判別する。
  // 第二引数の acceptedPlans には、利用可能とみなすプランを指定することができる。
  // 省略した場合、 NotSet でなければ利用可能と判定される。
  isProductAvailable(
    productCode: ProductCodeTypeLiterals,
    acceptedPlans: PlanTypeLiterals[] = [],
  ): boolean {
    const isAcceptedPlan = (plan: PlanTypeLiterals) => {
      return acceptedPlans.length === 0
        ? plan !== "NotSet"
        : acceptedPlans.includes(plan);
    };
    return (
      !!this.data &&
      this.data.orgProducts.some(
        (v) => v.productCode === productCode && isAcceptedPlan(v.plan),
      ) &&
      !this.shouldShowSettingAlone()
    );
  }

  // Enterprise 向け製品のみを利用しているかを判別する
  // AI Studio のみ、もしくは AI Studio + ServiceAccount のみの場合に true を返す
  // 設定・サポートのサイドメニュー等の表示判定(e.g. お知らせは表示しないなど)目的以外に使うべきではない
  // あくまで今後コンソールを分離する(かも)までの一時的な対応です
  // ref. https://github.com/graffer-inc/cloud-gov/pull/18705
  isOnlyUsingEnterpriseProduct(): boolean {
    if (!this.data?.orgProducts) {
      return false;
    }

    const enableProducts = this.data.orgProducts.reduce((filtered, v) => {
      if (v.plan !== "NotSet") {
        filtered.push(v.productCode);
      }
      return filtered;
    }, [] as string[]);

    if (enableProducts.length === 1) {
      return enableProducts[0] === "AIStudio";
    }

    if (enableProducts.length === 2) {
      return (
        enableProducts.includes("AIStudio") &&
        enableProducts.includes("ServiceAccount")
      );
    }

    return false;
  }

  /**
   * ユーザーのシステムレベルロールがアカウント管理者ロールのみの場合、製品設定に関わらず「設定・サポート」のページしか開けないようにする。
   * 本来はpermissionの仕組みで制御できるようにするべきだが、今のところpermissionと製品の紐づきは明示的なものではないため、一旦短期的にこのような実装を行う。
   */
  private shouldShowSettingAlone(): boolean {
    return this.hasRole("AccountManager") && this.data!.roles.length === 1;
  }

  /**
   * hasProcManagementRelatedPermission
   *
   * 手続き管理関連の権限解決を行うための、一時対応のメソッド。
   *
   * group delegatedなpermissionとintersectする必要がある点、(procedure関連のデータを、GroupPermissionAttachedを実装したclassにする修正が必要)
   * またgroupに所属していないservice provider accountの権限も解決する必要がある点から、(checkGroupPermissionを、service provider accountもパスできるようにする修正が必要)
   * 臨時で切り出した。
   *
   * 下記権限表の、「ステータス変更（下書き -> 公開）」などで考慮していく必要がある。
   * https://docs.google.com/spreadsheets/d/1lU2oZetLH2AIFvbxv0CtR5CAAo3EBxcz8ABnsRQNy5g/edit#gid=0
   * あくまで臨時対応なので、追って、当該パターンなどを考慮できる、一貫した方式での修正を目指していく。
   */
  private hasProcManagementRelatedPermission(
    permission: GrafferPermissions,
  ): boolean {
    if (!this.data?.permissions?.includes(permission)) {
      return false;
    }

    if (this.isServiceProviderAccount() || this.hasRole("Admin")) {
      return true;
    }

    for (const groupId of this.groupIDs) {
      if (
        (this.data?.groupDelegatedPermissions[groupId] || []).includes(
          permission,
        )
      ) {
        return true;
      }
    }

    return false;
  }

  get orgId() {
    return this.data?.orgId || "";
  }

  get orgSlug() {
    return this.data?.orgSlug || "";
  }

  get groupIDs() {
    return this.data?.groupIds || [];
  }
}
